/**
 * SupportBubble — Intercom-style floating help messenger.
 *
 * Bottom-right launcher pill that opens a compact floating panel
 * (NOT a full-height side drawer). Three views accessible via a
 * bottom nav:
 *   • Home     — greeting, search, popular articles, send-message CTA
 *   • Help     — full article browser with search & categories
 *   • Messages — submit a ticket / contact form
 *
 * Hidden when the CMS toggle `support_enabled` is off, and on routes
 * where a floating bubble would be intrusive (CMS, TV leaderboard).
 */
import { useEffect, useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  MessageCircle, X, Search, ChevronLeft, ChevronRight, Send,
  Home, BookOpen, MessageSquarePlus, Sparkles, ArrowRight, Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
// Lazy-load ArticleBody: it pulls DOMPurify (~15KB gz) which is only
// needed when a user actually opens a support article. Loading it on
// demand keeps the support bubble icon (rendered on every page) light.
const ArticleBody = lazy(() =>
  import('./ArticleBody').then(m => ({ default: m.ArticleBody }))
);
import { getClientMeta } from '@/lib/clientInfo';

// Global event other components fire to open the bubble straight to a view.
// Used by `UnifiedAuthPage` ("Request access" link) and anywhere we replace
// a `mailto:` with an in-app message flow. Detail: `{view?: 'messages'|'home'|'help'}`.
export const SUPPORT_OPEN_EVENT = 'sportsweeps:open-support';
export function openSupportBubble(view = 'messages') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SUPPORT_OPEN_EVENT, { detail: { view } }));
}

const PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api/public/support`;
// Routes where the help bubble must NEVER render:
//   /cms/*                 — super-admin area has its own internal channels
//   /tv/*                  — public TV displays should be fully chrome-free
//   /joinpool/:code/picks  — team selection (the WarRoom). The picks page
//                            uses a sticky bottom dock for the "Submit Team"
//                            button; the bubble sits on top of it on mobile.
//   /joinpool/:code/war-room — legacy alias for the same WarRoom page
//   /war-room/*            — older direct route (kept for safety)
//   /joinpool/:code/lms[/pick] — football war room + team selection: the pick
//                            grid and the sticky action bar leave no room for
//                            the bubble on mobile.
//   /joinpool/:code/racing — racing war room, same reason.
const HIDDEN_ROUTES = [
  /^\/cms(?:\/|$)/,
  /^\/tv\//,
  /^\/joinpool\/[^/]+\/picks(?:\/|$)/,
  /^\/joinpool\/[^/]+\/war-room(?:\/|$)/,
  /^\/joinpool\/[^/]+\/lms(?:\/|$)/,
  /^\/joinpool\/[^/]+\/racing(?:\/|$)/,
  /^\/war-room\//,
];
const HEADER_GRADIENT = 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)';
const SKELETON_DELAY_MS = 250; // Don't flash skeletons for fast responses.

// Module-scope cache so the bubble only fetches articles once per page load.
// Avoids the "skeletons flash on first open" effect on mobile where the spring
// animation + a fresh network round-trip overlap.
let _articlesCache = null;
let _articlesPromise = null;

function fetchArticlesCached() {
  if (_articlesCache) return Promise.resolve(_articlesCache);
  if (_articlesPromise) return _articlesPromise;
  _articlesPromise = axios.get(`${PUBLIC}/articles`)
    .then(({ data }) => { _articlesCache = data; return data; })
    .catch(() => ({ articles: [], categories: [] }))
    .finally(() => { _articlesPromise = null; });
  return _articlesPromise;
}

/** Hook: tracks loading state but only flips to `true` after a short delay,
 *  so quick responses (cache hits / fast networks) never render a skeleton. */
function useDeferredLoading(isLoading, delayMs = SKELETON_DELAY_MS) {
  const [shouldShow, setShouldShow] = useState(false);
  useEffect(() => {
    if (!isLoading) { setShouldShow(false); return undefined; }
    const t = setTimeout(() => setShouldShow(true), delayMs);
    return () => clearTimeout(t);
  }, [isLoading, delayMs]);
  return shouldShow;
}

export function SupportBubble() {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('home');
  const [selectedSlug, setSelectedSlug] = useState(null);

  const hidden = HIDDEN_ROUTES.some((r) => r.test(pathname));

  // Deep-link sync: `?article=<slug>` opens the bubble on Help view at that
  // article. Used for shareable links from email replies / docs. We read this
  // exactly once per pathname change so an external nav (e.g. the user closing
  // the bubble) doesn't trigger an open-loop while the URL is being cleaned.
  useEffect(() => {
    if (hidden) return;
    const slug = searchParams.get('article');
    if (slug) {
      setView('help');
      setSelectedSlug(slug);
      setOpen(true);
    }
    // We intentionally depend only on `pathname` — the searchParams change
    // when WE strip the param after close, which would otherwise re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hidden]);

  const updateArticleParam = (slug) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (slug) next.set('article', slug);
      else next.delete('article');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (hidden) { setEnabled(false); return; }
    let cancelled = false;
    axios.get(`${PUBLIC}/config`)
      .then(({ data }) => {
        if (cancelled) return;
        setEnabled(!!data.support_enabled);
        // Warm the articles cache as soon as the bubble decides it's enabled —
        // by the time the user clicks open, content is already in memory and
        // there's no skeleton flash on the first reveal animation.
        if (data.support_enabled) fetchArticlesCached();
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [hidden, pathname]);

  // Listen for the global "open support" event so external components
  // (e.g. UnifiedAuthPage "Request access" link) can pop the messenger
  // open without prop-drilling a setOpen down through the tree.
  useEffect(() => {
    const handler = (e) => {
      if (hidden) return;
      const nextView = e?.detail?.view;
      if (nextView) setView(nextView);
      setSelectedSlug(null);
      setOpen(true);
    };
    window.addEventListener(SUPPORT_OPEN_EVENT, handler);
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, handler);
  }, [hidden]);

  if (!enabled) return null;

  const handleClose = () => { setOpen(false); setSelectedSlug(null); updateArticleParam(null); };
  const goToArticle = (slug) => { setView('help'); setSelectedSlug(slug); updateArticleParam(slug); };
  const clearArticle = () => { setSelectedSlug(null); updateArticleParam(null); };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="messenger"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="fixed z-[60] bottom-24 right-5 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[640px] max-h-[calc(100vh-7rem)] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden border border-gray-200"
            data-testid="support-messenger"
          >
            <MessengerView
              view={view}
              selectedSlug={selectedSlug}
              setSelectedSlug={clearArticle}
              onClose={handleClose}
              goToArticle={goToArticle}
              onSwitchView={(v) => { setView(v); clearArticle(); }}
              pathname={pathname}
            />
            <BottomNav active={view} onChange={(v) => { setView(v); clearArticle(); }} />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close help' : 'Open help'}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center"
        style={{ background: HEADER_GRADIENT }}
        data-testid="support-bubble-launcher"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </>
  );
}

/* ─── Top-level view router inside the messenger ─── */
function MessengerView({ view, selectedSlug, setSelectedSlug, onClose, goToArticle, onSwitchView, pathname }) {
  if (selectedSlug) {
    return <ArticleDetail slug={selectedSlug} onBack={() => setSelectedSlug(null)} onClose={onClose} />;
  }
  if (view === 'home') return <HomeView onClose={onClose} goToArticle={goToArticle} onSwitchView={onSwitchView} />;
  if (view === 'help') return <HelpView onClose={onClose} goToArticle={goToArticle} />;
  if (view === 'messages') return <MessagesView onClose={onClose} pathname={pathname} onSwitchView={onSwitchView} />;
  return null;
}

/* ─── Bottom navigation (Home / Help / Messages) ─── */
function BottomNav({ active, onChange }) {
  const items = [
    { key: 'home', label: 'Home', Icon: Home },
    { key: 'help', label: 'Help', Icon: BookOpen },
    { key: 'messages', label: 'Messages', Icon: MessageSquarePlus },
  ];
  return (
    <div className="border-t border-gray-100 bg-white grid grid-cols-3" data-testid="support-bottom-nav">
      {items.map(({ key, label, Icon }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className="flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors"
            data-testid={`support-nav-${key}`}
          >
            <Icon
              className="w-5 h-5"
              style={{ color: isActive ? '#6C3CE0' : '#9ca3af' }}
              strokeWidth={isActive ? 2.4 : 1.8}
            />
            <span className="text-[10px] font-semibold tracking-wide" style={{ color: isActive ? '#6C3CE0' : '#9ca3af' }}>
              {label.toUpperCase()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Home view: greeting + popular articles + chat CTA ─── */
function HomeView({ onClose, goToArticle, onSwitchView }) {
  const [data, setData] = useState(_articlesCache || { articles: [], categories: [] });
  const [loading, setLoading] = useState(!_articlesCache);
  const showSkeleton = useDeferredLoading(loading);

  useEffect(() => {
    if (_articlesCache) { setLoading(false); return undefined; }
    let cancelled = false;
    fetchArticlesCached()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const popular = data.articles.slice(0, 4);

  return (
    <>
      {/* Hero greeting */}
      <div className="relative px-5 pt-6 pb-5" style={{ background: HEADER_GRADIENT }}>
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-white/80 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <Sparkles className="w-5 h-5 text-white/80 mb-2" />
        <h2 className="text-2xl font-bold text-white leading-tight">Hello there.</h2>
        <h3 className="text-2xl font-bold text-white/85 leading-tight mt-0.5">How can we help?</h3>
      </div>

      {/* Cards stack cleanly below the gradient (no overlap) so the first
          card title is never clipped by the scroll container boundary. */}
      <div className="px-4 pt-3 pb-3 space-y-3 flex-1 min-h-0 overflow-y-auto bg-gray-50">
        {/* Quick actions card */}
        <button
          onClick={() => onSwitchView('messages')}
          className="w-full bg-white rounded-xl shadow-md border border-gray-100 px-4 py-3.5 flex items-center justify-between hover:shadow-lg transition-all group"
          data-testid="support-home-send-message"
        >
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">Send us a message</p>
            <p className="text-xs text-gray-500 mt-0.5">We typically reply within one business day</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all" />
        </button>

        {/* Search hint card */}
        <button
          onClick={() => onSwitchView('help')}
          className="w-full bg-white rounded-xl shadow-md border border-gray-100 px-4 py-3 flex items-center gap-3 hover:shadow-lg transition-all"
          data-testid="support-home-search"
        >
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-500 flex-1 text-left">Search for help</span>
        </button>

        {/* Popular articles */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-4 pt-3 pb-2">
            <p className="text-[11px] font-bold tracking-wider text-gray-500 uppercase">Popular articles</p>
          </div>
          {showSkeleton ? (
            <div className="px-4 pb-3 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : loading ? (
            <div className="px-4 pb-3" style={{ minHeight: 124 }} />
          ) : popular.length === 0 ? (
            <p className="text-xs text-gray-400 px-4 pb-4">No articles yet.</p>
          ) : (
            <div>
              {popular.map((a) => (
                <button
                  key={a.id}
                  onClick={() => goToArticle(a.slug)}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 border-t border-gray-100 first:border-t-0"
                  data-testid={`support-home-article-${a.slug}`}
                >
                  <span className="text-sm text-gray-800 truncate">{a.title}</span>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              ))}
              <button
                onClick={() => onSwitchView('help')}
                className="w-full text-left px-4 py-2.5 text-xs font-semibold text-purple-600 hover:bg-purple-50 border-t border-gray-100"
                data-testid="support-home-see-all"
              >
                See all articles →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Help view: full search + categories + article list ─── */
function HelpView({ onClose, goToArticle }) {
  const [data, setData] = useState({ articles: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const load = useCallback((q = '') => {
    setLoading(true);
    axios.get(`${PUBLIC}/articles`, { params: q ? { search: q } : {} })
      .then(({ data }) => setData(data))
      .catch(() => setData({ articles: [], categories: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (activeCategory) return data.articles.filter((a) => a.category_id === activeCategory);
    return data.articles;
  }, [data.articles, activeCategory]);

  return (
    <>
      <ViewHeader title="Help" onClose={onClose} />
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
            placeholder="Search help articles…"
            className="pl-9"
            data-testid="support-search-input"
          />
        </div>
        {data.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <CategoryPill active={activeCategory === null} onClick={() => setActiveCategory(null)} testId="support-cat-all">All</CategoryPill>
            {data.categories.map((c) => (
              <CategoryPill key={c.id} active={activeCategory === c.id} onClick={() => setActiveCategory(c.id)} testId={`support-cat-${c.slug}`}>
                {c.name}
              </CategoryPill>
            ))}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 px-2 pb-2 mt-2">
        {loading && (
          <div className="space-y-2 px-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-10 px-4">
            No matching articles. Try the Messages tab to ask us directly.
          </p>
        )}
        {!loading && filtered.map((a) => (
          <button
            key={a.id}
            onClick={() => goToArticle(a.slug)}
            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-50 flex items-start gap-3 transition-colors"
            data-testid={`support-article-${a.slug}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
              {a.summary && <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{a.summary}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
          </button>
        ))}
      </ScrollArea>
    </>
  );
}

function CategoryPill({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/* ─── Article detail view ─── */
function ArticleDetail({ slug, onBack, onClose }) {
  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${PUBLIC}/articles/${slug}`)
      .then(({ data }) => setArt(data))
      .catch(() => setArt(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <button onClick={onBack} className="text-purple-600 hover:bg-purple-50 rounded-md p-1" data-testid="support-article-back" aria-label="Back">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs font-semibold text-gray-700 flex-1 truncate">Back to articles</span>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      <ScrollArea className="flex-1 px-5 pb-5">
        {loading && <Skeleton className="h-32 w-full mt-3" />}
        {!loading && !art && <p className="text-sm text-gray-500 py-8 text-center">This article is no longer available.</p>}
        {!loading && art && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-2 mt-3">{art.title}</h2>
            {art.summary && <p className="text-sm text-gray-600 mb-4">{art.summary}</p>}
            <Suspense fallback={<Skeleton className="h-32 w-full mt-3" />}>
              <ArticleBody html={art.body_html} />
            </Suspense>
          </>
        )}
      </ScrollArea>
    </>
  );
}

/* ─── Messages view: contact form ─── */
function MessagesView({ onClose, pathname, onSwitchView }) {
  const [form, setForm] = useState({ email: '', access_code: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.subject || !form.message) {
      toast.error('Please fill in email, subject and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${PUBLIC}/tickets`, {
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        access_code: form.access_code.trim() || null,
        page_url: pathname,
        client_meta: getClientMeta(),
      });
      setSubmitted(true);
      setForm({ email: '', access_code: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <ViewHeader title="Messages" onClose={onClose} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="support-message-success">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: HEADER_GRADIENT }}>
            <Send className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Message sent</h3>
          <p className="text-sm text-gray-500 mt-2 max-w-xs">
            Thanks - we got it. We'll reply by email within one business day.
          </p>
          <button
            onClick={() => { setSubmitted(false); onSwitchView('home'); }}
            className="mt-6 text-xs font-semibold text-purple-600 hover:text-purple-700"
          >
            Back to home →
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <ViewHeader title="Messages" onClose={onClose} />
      <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="support-contact-form">
        <p className="text-sm text-gray-600">We typically reply within one business day.</p>
        <Field label="Your email" required>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@example.com"
            required
            data-testid="support-form-email"
          />
        </Field>
        <Field label="Pool access code" optional>
          <Input
            value={form.access_code}
            onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase() })}
            placeholder="e.g. 4T499Y"
            maxLength={20}
            style={{ fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.1em' }}
            data-testid="support-form-access-code"
          />
        </Field>
        <Field label="Subject" required>
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Short summary"
            required
            maxLength={200}
            data-testid="support-form-subject"
          />
        </Field>
        <Field label="Message" required>
          <Textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell us what's happening…"
            rows={5}
            required
            maxLength={5000}
            data-testid="support-form-message"
          />
        </Field>
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-md disabled:opacity-60"
          style={{ background: HEADER_GRADIENT }}
          data-testid="support-form-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </>
  );
}

function ViewHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function Field({ label, required, optional, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
        {optional && <span className="text-gray-400 font-normal"> (optional)</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
